<template>
  <div class="w-100">
    <AcknowledgementSettingsSkeleton v-if="pageLoading" />

    <div
      v-else
      class="form-wrapper"
    >
      <v-form
        class="acknowledgements-form"
        @submit.prevent="update"
      >
        <div>
          <h2 class="text-h2">
            Job Profile Acknowledgement Settings
          </h2>
          <p class="text-body-2 text-textLight">
            Customize the default information and language settings for your
            <router-link
              class="page-description-link"
              :to="employeeAcknowledgementsRoute"
            >
              job profile acknowledgement
            </router-link>
            workflow.
          </p>
        </div>
        <div
          v-for="fieldConfig in acknowledgementFormFieldConfigs"
          :key="fieldConfig.key"
        >
          <DpplFormField
            :field-name="fieldConfig.key"
            :form="form"
            v-bind="$attrs"
            @update:model-value="handleFormValueUpdate($event)"
          >
            <template #field="{ props, on }">
              <DpplTextField
                v-if="fieldConfig.control === 'text'"
                :custom-label="labels[fieldConfig.key]"
                v-bind="props"
                :placeholder="DEFAULT_LANGUAGE_PLACEHOLDER"
                :type="fieldConfig.numeric ? 'text' : undefined"
                :inputmode="fieldConfig.numeric ? 'numeric' : undefined"
                :disabled="componentLoading"
                v-on="on"
              >
                <template #description>
                  <p class="text-body-3 text-textDark field-hint">
                    <template
                      v-for="(hintSegment, hintSegmentIndex) in getHintSegmentsForField(fieldConfig.key)"
                      :key="hintSegmentIndex"
                    >
                      <router-link
                        v-if="hintSegment.type === 'link'"
                        class="page-description-link"
                        :to="hintSegment.to"
                      >
                        {{ hintSegment.text }}
                      </router-link>
                      <template v-else>
                        {{ hintSegment.text }}
                      </template>
                    </template>
                  </p>
                </template>
              </DpplTextField>
              <DpplTextArea
                v-else
                v-bind="props"
                :placeholder="DEFAULT_LANGUAGE_PLACEHOLDER"
                :custom-label="labels[fieldConfig.key]"
                :rows="fieldConfig.rows || 3"
                :disabled="componentLoading"
                v-on="on"
              >
                <template #description>
                  <p class="text-body-3 text-textDark field-hint">
                    <template
                      v-for="(hintSegment, hintSegmentIndex) in getHintSegmentsForField(fieldConfig.key)"
                      :key="hintSegmentIndex"
                    >
                      <router-link
                        v-if="hintSegment.type === 'link'"
                        class="page-description-link"
                        :to="hintSegment.to"
                      >
                        {{ hintSegment.text }}
                      </router-link>
                      <template v-else>
                        {{ hintSegment.text }}
                      </template>
                    </template>
                  </p>
                </template>
              </DpplTextArea>
            </template>
          </DpplFormField>
        </div>

        <div class="mt-4">
          <DpplAutoSizedButton
            :disabled="!isFormValid || componentLoading"
            :loading="componentLoading"
            @click="update"
          >
            Update
          </DpplAutoSizedButton>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { isEmpty } from 'lodash-es';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import DpplTextField from '@/components/datapeople/DpplTextField.vue';
import DpplTextArea from '@/components/datapeople/DpplTextArea.vue';
import DpplFormField from '@/components/datapeople/DpplFormField.vue';
import AcknowledgementSettingsSkeleton from '@/components/settings/components/AcknowledgementSettingsSkeleton.vue';
import { useAppLoading } from '@/stores/app/loading';
import { useAcknowledgementSettings } from '@/stores/settings/acknowledgementSettings';
import { useAcknowledgementSettingsForm } from '@/stores/settings/acknowledgementSettingsForm';

const employeeAcknowledgementsRoute = { name: 'employee-acknowledgements' };

const DEFAULT_LANGUAGE_PLACEHOLDER = 'Revert to default (Click update to view)';

const acknowledgementFieldHints = {
  company_name: `The way your company will be referred to in job profile acknowledgement 
    communications (e-mails). (Ex. "{Company} requests that you acknowledge your job profile.")`,
  hr_contact_email: `The e-mail address your employees will be instructed to contact 
    with any questions regarding job profile acknowledgements.`,
  acknowledgement_cycle_sla: [
    {
      type: 'text',
      text: `The number of days after which you require employees to sign a new acknowledgment. 
        You will receive an alert on the `,
    },
    {
      type: 'link',
      text: 'Acknowledgements page',
      to: employeeAcknowledgementsRoute,
    },
    {
      type: 'text',
      text: ` when any employee is overdue on their signature. (Ex. If you require employees to 
        sign an acknowledgement each year, the Acknowledgement Cycle SLA would be 365 days.)`,
    },
  ],
  acknowledgement_pending_sla: `The number of days an employee has left an acknowledgment request pending after 
    which you would like to be alerted. (Ex. If you want to be alerted if an employee doesn't sign their 
    acknowledgement after 7 days, the Acknowledgement Pending SLA would be 7 days.)`,
  acknowledgement_email_intro: `The language used in the acknowledgement request email to explain what is being 
    requested of them. Update if you have preferred language or your Legal team requires certain language.`,
  acknowledgement_agreement_text: `The language used in the acknowledgement form that the employee will sign, 
    explaining what they are acknowledging. Update if you have preferred language or your Legal team requires 
    certain language.`,
  acknowledgement_pdf_text: `The language included along with the employee's signature in the completed 
    acknowledgement file. Update if you have preferred language or your Legal team requires certain language.`,
};

const getHintSegmentsForField = (fieldKey) => {
  const hint = acknowledgementFieldHints[fieldKey];

  if (typeof hint === 'string') {
    return [{ type: 'text', text: hint }];
  }

  return hint || [];
};

const acknowledgementFormFieldConfigs = [
  { key: 'company_name', control: 'text' },
  { key: 'hr_contact_email', control: 'text' },
  { key: 'acknowledgement_cycle_sla', control: 'text', numeric: true },
  { key: 'acknowledgement_pending_sla', control: 'text', numeric: true },
  { key: 'acknowledgement_email_intro', control: 'textarea', rows: 4 },
  { key: 'acknowledgement_agreement_text', control: 'textarea', rows: 3 },
  { key: 'acknowledgement_pdf_text', control: 'textarea', rows: 3 },
];

const numericInputFieldKeys = new Set(
  acknowledgementFormFieldConfigs
    .filter((config) => config.numeric).map((config) => config.key),
);

const appLoadingStore = useAppLoading();
const { componentLoading, pageLoading } = storeToRefs(appLoadingStore);

const acknowledgementSettingsForm = useAcknowledgementSettingsForm();
const {
  values,
  errors,
  errorMessages,
  labels,
  isFormValid,
} = storeToRefs(acknowledgementSettingsForm);

const acknowledgementSettings = useAcknowledgementSettings();
const { load, update } = acknowledgementSettings;

const form = computed(() => ({
  values: values.value,
  errors: errors.value,
  errorMessages: errorMessages.value,
  labels: labels.value,
}));

const handleFormValueUpdate = (payload) => {
  if (isEmpty(payload || {})) return;

  const newValue = { ...payload };

  numericInputFieldKeys.forEach((key) => {
    if (key in newValue) {
      const value = newValue[key];
      const str = value === null || value === undefined ? '' : String(value);

      newValue[key] = str.replace(/\D/g, '');
    }
  });

  acknowledgementSettingsForm.setFormValues(newValue);
};

onMounted(async () => {
  await load();
});
</script>

<style scoped lang="less">
.form-wrapper {
  border-radius: 10px;
  background: @white;
  padding: 28px 32px 24px;
}

.acknowledgements-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: flex-start;
  justify-content: flex-start;
  max-width: 720px;
  width: 100%;
}

.page-description-link,
.page-description-link:visited {
  font-weight: 500;
  color: @text-brand;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
  }
}

.field-hint {
  font-weight: 400 !important;
  margin: 6px 0 !important;
}
</style>
